import { ProofDoc } from '~/screens/review-and-view-terms/components/proof-doc-display/proof-doc-display.component';
import { fetchFunnelPacket, fetchLmiZoneLookup, fetchLMIProofDocs } from '~/services';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { SPONSORED_SOLAR_ROUTES } from '~/utils/constants';
import { DocusignSignedStatus, store } from '~/utils/store';
import { FunnelRoutes, ValidationResult } from './types';
import { startSynchronousValidationChain } from './utils';

/**
 * @returns an object with a getNextRoute function that returns the next route
 * based on the funnel packet.
 *
 * @Note: This is not a God Controller that decides every
 * aspect of routing in flex-funnel(or even sponsored solar). This only moderates
 * app routing when the browser is reloaded. This is to prevent users from
 * skipping steps in the funnel by directly navigating to a later step.
 * Individual components/steps will still have freedom to route anywhere
 * they want depending upon the business logic.
 *
 * for example, if a utility-connect step wants to route to proof doc instead of
 * review-form step because some business logic changed, it can do so freely without
 * being moderated by this controller. This contorller is more of a safety net than
 * a primary router.
 *
 * This is intentionally built to reduce complexity. When this new type of route-based
 * funnels scales, (i.e) we have more funnels like sponsored solar, we don't
 * want to build a monolithic router controller that makes decisions for everything
 * in the system A.K.A build another solar funnel.
 * */
export function SponsoredSolarRouterController() {
  let isUserGeoVerified = false;
  let lmiProofDocs: ProofDoc[] = [];
  let funnelPacket: FetchFunnelPacketResponse;
  let userId: number | undefined;

  function isUserSignedSelfAttestation() {
    const docusignUserStatus =
      store.get('DOCUSIGN_USER_SIGNED_STATUS') ||
      funnelPacket?.user?.originationSource?.queryParams?.docusignSignedStatus;
    const docusignSignedStatus: DocusignSignedStatus[] = [
      'signing_complete',
      'cancel',
      'decline',
      'viewing_complete',
    ];
    return docusignSignedStatus.includes(docusignUserStatus as DocusignSignedStatus);
  }

  async function getFunnelPacket(userId: number) {
    try {
      return await fetchFunnelPacket({ userId });
    } catch (error) {
      error.message = `Unable to Fetch Funnel Packet when deciding route: ${error.message}`;
      throw error;
    }
  }

  async function getUserGeoVerificationStatus(funnelPacket) {
    let isUserGeoVerified = false;
    try {
      const response = await fetchLmiZoneLookup({
        utilityAccountId: funnelPacket?.utilityAccount.id,
      });
      const { isInLmiArea, isInCensusTract, error } = response;
      if (error) {
        store.set('IS_IN_LMI_ZONE', false);
      }
      isUserGeoVerified = isInLmiArea || isInCensusTract;
      store.set('IS_IN_LMI_ZONE', isUserGeoVerified);
    } catch (error) {
      error.message = `Unable to Fetch LMI Zone options when deciding route: ${error.message}`;
      store.set('IS_IN_LMI_ZONE', false);
      throw error;
    }
    return isUserGeoVerified;
  }

  async function getLmiProofDocs(userId: number) {
    try {
      const response = await fetchLMIProofDocs({ id: userId });
      return response.lmiProofDocs;
    } catch (e) {
      e.message = `Unable to Fetch LMI Proof Docs when deciding route: ${e.message}`;
      throw e;
    }
  }

  async function getEssentials() {
    userId = store.get('USER_ID');
    if (!userId) return;
    funnelPacket = await getFunnelPacket(userId);
    if (funnelPacket) {
      isUserGeoVerified = await getUserGeoVerificationStatus(funnelPacket);
      lmiProofDocs = await getLmiProofDocs(userId);
    }
  }

  function validateReviewAndViewTerms(): ValidationResult {
    const userId = store.get('USER_ID');
    const result: ValidationResult = {
      isValid: false,
      route: SPONSORED_SOLAR_ROUTES.REVIEW_AND_VIEW_TERMS as FunnelRoutes,
    };
    const proofDocSkipped =
      !!store.get('PROOF_DOC_SKIPPED') ||
      !!funnelPacket?.user?.originationSource?.queryParams?.proofDocSkipped;
    const hasProofDocs = lmiProofDocs?.length > 0;

    if (!userId) return result;
    if (isUserGeoVerified) {
      return { ...result, isValid: proofDocSkipped || hasProofDocs };
    }

    return {
      ...result,
      isValid: hasProofDocs || isUserSignedSelfAttestation(),
    };
  }

  function validateReviewLmiForm(): ValidationResult {
    const userId = store.get('USER_ID');
    const result: ValidationResult = {
      isValid: false,
      route: SPONSORED_SOLAR_ROUTES.REVIEW_LMI_FORM as FunnelRoutes,
    };

    if (userId) {
      const isUserSignedEnvelope = isUserSignedSelfAttestation();

      const isProofDocSkipped =
        store.get('PROOF_DOC_SKIPPED') ||
        funnelPacket?.user?.originationSource?.queryParams?.proofDocSkipped;
      return {
        ...result,
        isValid: !!(!isUserGeoVerified && !isUserSignedEnvelope && isProofDocSkipped),
      };
    }
    return result;
  }

  function validateProofDocUpload(): ValidationResult {
    const userId = store.get('USER_ID');
    const result: ValidationResult = {
      isValid: false,
      route: SPONSORED_SOLAR_ROUTES.PROOF_DOCS_UPLOAD as FunnelRoutes,
    };
    if (userId) {
      try {
        return {
          ...result,
          isValid: funnelPacket?.utilityCredential?.verificationStatus === 'correct',
        };
      } catch (error) {
        error.message = `Unable to Fetch Funnel Packet when deciding route: ${error.message}`;
        throw error;
      }
    }
    return result;
  }

  function validateUtilityConnect(): ValidationResult {
    const userId = store.get('USER_ID');
    const result: ValidationResult = {
      isValid: false,
      route: SPONSORED_SOLAR_ROUTES.UTILITY_CONNECT as FunnelRoutes,
    };
    if (userId) {
      try {
        return {
          ...result,
          isValid: !!funnelPacket?.user?.id,
        };
      } catch (error) {
        error.message = `Unable to Fetch Funnel Packet when deciding route: ${error.message}`;
        throw error;
      }
    }
    return result;
  }

  function validateGoogleUser(): ValidationResult {
    return {
      isValid: !!store.get('GOOGLE_USER'),
      route: SPONSORED_SOLAR_ROUTES.GOOGLE_USER as FunnelRoutes,
    };
  }

  function validateSignup(): ValidationResult {
    return {
      isValid: !!store.get('UTILITY_SELECTION') && !!store.get('ZIP_CODE'),
      route: SPONSORED_SOLAR_ROUTES.SIGNUP as FunnelRoutes,
    };
  }

  function validateWelcomeBack(): ValidationResult {
    return {
      isValid:
        !!store.get('RESUMING_IN_PROGRESS') && !!store.get('RESUMING_USER_UTILITY_ACCOUNT_ID'),
      route: SPONSORED_SOLAR_ROUTES.WELCOME_BACK as FunnelRoutes,
    };
  }

  function validateToDefault(): ValidationResult {
    return {
      isValid: true,
      route: SPONSORED_SOLAR_ROUTES.UTILITY_SELECTION as FunnelRoutes,
    };
  }

  return {
    getNextRoute: async (): Promise<FunnelRoutes> => {
      userId = store.get('USER_ID');
      await getEssentials();
      const nextRoute = startSynchronousValidationChain(
        validateWelcomeBack,
        validateReviewAndViewTerms,
        validateReviewLmiForm,
        validateProofDocUpload,
        validateUtilityConnect,
        validateGoogleUser,
        validateSignup,
        validateToDefault,
      );
      return nextRoute?.route || validateToDefault().route;
    },
  };
}
